// GallerySection.jsx — unified selection + confirm delete + move dialog (Tailwind, no libs)

import React, { useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CrumbsGrid from './CrumbsGrid';
import FolderGrid from './FolderGrid';
import ImageGrid from './ImageGrid';
import VideoGrid from './VideoGrid';
import PdfGrid from './PdfGrid';
import ContextMenu from "./ContextMenu";
import MoveDialog from "../Uploading/MoveDialog";
import InlineRenamePopover from './InlineRenamePopover';

/* --- Compact confirm modal (no libs) --- */
function useConfirmModal() {
    const [open, setOpen] = useState(false);
    const resolverRef = React.useRef(null);

    function confirm() {
        setOpen(true);
        return new Promise((resolve) => (resolverRef.current = resolve));
    }

    function resolve(v) { resolverRef.current?.(v); setOpen(false); }
    return { open, confirm, resolve };
}

function ConfirmModal({ open, title = 'Видалити вибране?', description = 'Дію не можна скасувати.', onCancel, onConfirm, danger = true }) {
    const dialogRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [open, onCancel]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
            <div ref={dialogRef} role="dialog" aria-modal className="relative z-10 w-full max-w-sm rounded-xl border bg-white p-4 shadow-2xl dark:bg-neutral-900">
                <h2 className="text-sm font-semibold">{title}</h2>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{description}</p>
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button onClick={onCancel} className="rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-50">Скасувати</button>
                    <button onClick={onConfirm} className={(danger ? 'bg-red-600 hover:bg-red-700 focus:ring-red-400 ' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-400 ') + 'rounded-lg px-3 py-1.5 text-xs font-semibold text-white focus:outline-none focus:ring-2'}>Видалити</button>
                </div>
            </div>
        </div>
    );
}

export default function GallerySection({
    path, authed,
    folders, images, videos, pdfs, files,
    flatMode, loading,
    enterFolder, onOpenItem,
    onDeleteItems, onMoveItems,
    uploadLb, driveApi, setError, isReadOnly,

}) {
    const [selected, setSelected] = useState(() => new Set()); // `${type}:${id}`
    const [activeKey, setActiveKey] = useState(null);          // останній клік/відкритий айтем `${type}:${id}`    
    const [menu, setMenu] = useState(null); // {x,y,open}
    const [renameUi, setRenameUi] = useState(null);
    const confirm = useConfirmModal();
    const [moveOpen, setMoveOpen] = useState(false);
    const [moveCtx, setMoveCtx] = useState('');
    const containerRef = useRef(null);

    const keyOf = (type, id) => `${type}:${id}`;
    const isSelected = (type, id) => selected.has(keyOf(type, id));
    const selectOnly = (type, id) => setSelected(new Set([keyOf(type, id)]));
    const toggleSelect = (type, id) => setSelected(prev => { const n = new Set(prev); const k = keyOf(type, id); n.has(k) ? n.delete(k) : n.add(k); return n; });
    const clearSelection = () => setSelected(new Set());

    const hasSelection = selected.size > 0;
    const effectiveSelected = useMemo(() => {
        const s = new Set(selected);
        if (activeKey) s.add(activeKey);
        return s;
    }, [selected, activeKey]);
    const effectiveSelectedList = useMemo(
        () => Array.from(effectiveSelected).map(k => { const [type, id] = k.split(':'); return { type, id }; }),
        [effectiveSelected]
    );

    const currentFolderId = String(path?.[path.length - 1]?.id || '');
    const excludeIds = useMemo(() => {
        const ids = new Set([currentFolderId]);
        // відсіюємо кожну виділену (в т.ч. активну) папку
        for (const it of effectiveSelectedList) {
            if (it.type === 'folder') ids.add(String(it.id));
        }
        return Array.from(ids);
    }, [path, effectiveSelectedList]);

    // 🔹 LONG PRESS для мобільного контекстного меню
    const longPressTimerRef = useRef(null);
    const longPressPointRef = useRef(null);
    const longPressFiredRef = useRef(false);

    function startLongPress(touch) {
        // touch: Touch обʼєкт (clientX/clientY)
        longPressPointRef.current = { x: touch.clientX, y: touch.clientY };
        longPressFiredRef.current = false;

        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
        }

        longPressTimerRef.current = setTimeout(() => {
            longPressFiredRef.current = true;
            const pt = longPressPointRef.current;
            if (!pt) return;

            // шукаємо DOM-елемент під пальцем
            const el = document.elementFromPoint(pt.x, pt.y);
            if (!el) return;

            const host = el.closest('[data-sel-key]');
            const key = host?.getAttribute('data-sel-key');
            if (!key) return;

            const [type, id] = key.split(':');
            const item = getItemByIdSafe(id);
            if (!item) return;

            // відкриваємо те саме контекстне меню, що й на ПК
            itemHandlersFor(type).onContextMenu(item, {
                clientX: pt.x,
                clientY: pt.y,
                preventDefault() { },
            });
        }, 500); // 500ms — тривалість довгого тапу
    }

    function cancelLongPress() {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressPointRef.current = null;
    }

    useEffect(() => {
        // при unmount очищаємо таймер
        return () => {
            if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
        };
    }, []);


    useEffect(() => {
        const onKey = (e) => {
            // ігноруємо, якщо зараз ввід у формі
            const ae = document.activeElement;
            const aeTag = ae?.tagName?.toLowerCase();
            const typing = aeTag === 'input' || aeTag === 'textarea' || ae?.isContentEditable;

            // тригеримо тільки коли фокус усередині галереї
            const inContainer = containerRef.current?.contains(ae);

            if (!inContainer || typing) return;

            if (e.key === 'Escape') setMenu(null);
            if ((e.key === 'Delete' || e.key === 'Backspace') && hasSelection) {
                e.preventDefault();
                // відкриваємо підтвердження лише якщо воно ще не відкрите
                if (!confirm.open) openDeleteConfirm();
            }
            if (e.key === 'F2' && selected.size === 1) {
                e.preventDefault();
                const [only] = Array.from(selected);
                if (!only) return;
                const id = String(only).split(':')[1];
                const current = getItemById(id);
                const proposed = window.prompt('Нове ім’я', current?.name || '');
                if (!proposed) return;
                uploadLb.renameItem(id, proposed)
                    .then(() => uploadLb.loadList(path[path.length - 1].id))
                    .catch(err => setError?.(err.message || 'Rename failed'));
            }

        };
        const onDown = (e) => {
            if (!menu) return;
            if (!containerRef.current?.contains(e.target)) setMenu(null);
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('mousedown', onDown, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('mousedown', onDown, true);
        };
    }, [menu, hasSelection, confirm.open, selected, path, setError]);

    const getItemById = (id) => {
        const pools = [folders, images, videos, pdfs, files];
        for (const arr of pools) {
            const found = arr?.find(x => String(x.id) === String(id));
            if (found) return found;
        }
        return null;
    };

    async function openDeleteConfirm() {
        const ok = await confirm.confirm();
        if (!ok) return;
        if (typeof onDeleteItems === "function") {
            await onDeleteItems(effectiveSelectedList, {
                concurrency: 4, // можна 2–8, залежно від сервера/квот
                onProgress: (done, total) => {
                    // оновити свій індикатор, якщо є
                    console.log(`Видалено ${done}/${total}`);
                }
            });
        }
        clearSelection();
        setActiveKey(null);
    }

    function openMoveDialog() {
        const oldParentId = String(path?.[path.length - 1]?.id || '');
        setMoveCtx(oldParentId);
        setMoveOpen(true);
    }

    function itemHandlersFor(type) {
        return {
            onClick: (item, e) => {
                // toggle selection тільки з Ctrl/Cmd
                if (e.metaKey || e.ctrlKey) {
                    const k = keyOf(type, item.id);
                    setActiveKey(k);
                    return toggleSelect(type, item.id);
                }
                if (type === 'folder') {
                    // ЛКМ по папці/ярлику на папку — відкриваємо без виділення
                    clearSelection();
                    setActiveKey(null);

                    // Якщо це ярлик на теку — переходимо у targetId, але ім’я показуємо як у ярлика
                    const isShortcutFolder =
                        item?.type === 'application/vnd.google-apps.shortcut' &&
                        item.shortcut?.targetMimeType === 'application/vnd.google-apps.folder' &&
                        item.shortcut?.targetId;
                    if (isShortcutFolder) {
                        return enterFolder({ id: String(item.shortcut.targetId), name: String(item.name) });
                    }
                    return enterFolder(item);
                }
                // для файлів: одноразове виділення + ,tp відкриття
                const k = keyOf(type, item.id);
                selectOnly(type, item.id);
                setActiveKey(k);
                // onOpenItem?.(item, type);
            },
            onContextMenu: (item, e) => {
                e.preventDefault();
                const k = keyOf(type, item.id);
                if (!isSelected(type, item.id)) selectOnly(type, item.id);
                setActiveKey(k);
                setMenu({ x: e.clientX, y: e.clientY, open: true });
            },
            isSelected: (item) => isSelected(type, item.id),
        };
    }

    // всередині GallerySection (після useState/useRef)
    const [drag, setDrag] = useState(null); // {x0,y0,x1,y1, mode:'replace'|'toggle'}
    const [box, setBox] = useState(null);   // нормалізована {left,top,width,height}

    function normRect(x0, y0, x1, y1) {
        const left = Math.min(x0, x1);
        const top = Math.min(y0, y1);
        const width = Math.abs(x1 - x0);
        const height = Math.abs(y1 - y0);
        return { left, top, width, height, right: left + width, bottom: top + height };
    }

    function rectsIntersect(a, b) {
        return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
    }

    function hitTestSelection(selRect) {
        const root = containerRef.current;
        if (!root) return [];
        const nodes = root.querySelectorAll('[data-sel-key]');
        const result = [];
        nodes.forEach((el) => {
            const r = el.getBoundingClientRect();
            const rr = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
            if (rectsIntersect(selRect, rr)) {
                result.push(el.getAttribute('data-sel-key'));
            }
        });
        return result;
    }

    function onMouseDownContainer(e) {
        // тільки ЛКМ і лише якщо клацнули “по фону” секції, а не по кнопках/інпутах/посиланнях
        if (e.button !== 0) return;
        const tag = e.target.tagName.toLowerCase();
        if (['button', 'a', 'input', 'textarea', 'label', 'svg', 'path'].includes(tag)) return;

        // дозволяємо починати й поверх айтема — marquee все одно спрацює
        const mode = (e.ctrlKey || e.metaKey) ? 'toggle' : 'replace';
        setDrag({ x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY, mode });
        setBox({ left: e.clientX, top: e.clientY, width: 0, height: 0, right: e.clientX, bottom: e.clientY });
        // щоби не виділявся текст
        e.preventDefault();
    }

    useEffect(() => {
        if (!drag) return;

        const onMove = (e) => {
            const r = normRect(drag.x0, drag.y0, e.clientX, e.clientY);
            setBox(r);

            // лайтово: під час руху підсвічувати майбутній набір
            const keys = hitTestSelection(r);
            setSelected(prev => {
                if (drag.mode === 'replace') return new Set(keys);
                // toggle-тих, що в боксі, зберігаючи решту
                const next = new Set(prev);
                keys.forEach(k => next.has(k) ? next.delete(k) : next.add(k));
                return next;
            });
        };

        const onUp = () => {
            setDrag(null);
            setBox(null);
            // зробимо активним будь-який один з виділених (для однорідності)
            setActiveKey(prev => {
                if (prev) return prev;
                const any = Array.from(selected)[0];
                return any ?? null;
            });
        };

        document.addEventListener('mousemove', onMove, true);
        document.addEventListener('mouseup', onUp, true);
        return () => {
            document.removeEventListener('mousemove', onMove, true);
            document.removeEventListener('mouseup', onUp, true);
        };
    }, [drag]);

    function findItemRectById(id) {
        // шукаємо будь-яку ноду з атрибутом data-sel-key="type:id"
        // якщо ти не зберігаєш type, можна шукати по закінченню ":id"
        const node =
            document.querySelector(`[data-sel-key="folder:${id}"]`) ||
            document.querySelector(`[data-sel-key="image:${id}"]`) ||
            document.querySelector(`[data-sel-key="video:${id}"]`) ||
            document.querySelector(`[data-sel-key="pdf:${id}"]`) ||
            document.querySelector(`[data-sel-key$=":${CSS.escape(String(id))}"]`);
        return node?.getBoundingClientRect() || null;
    }

    function getItemByIdSafe(id) { try { return getItemById?.(id) || null; } catch { return null; } }

    async function submitRename(newName) {
        const { id } = renameUi || {};
        if (!id) { setRenameUi(null); return; }
        try {
            await uploadLb.renameItem(id, newName);
            await driveApi.loadList(path[path.length - 1].id);
        } catch (e) {
            setError?.(e.message || 'Rename failed');
        } finally {
            setRenameUi(null);
        }
    }

    return (
        <section
            ref={containerRef}
            className="relative"
            onMouseDown={onMouseDownContainer}
            // 🔹 мобільний long-press для контекстного меню
            onTouchStart={(e) => {
                if (e.touches.length !== 1) return;
                const touch = e.touches[0];
                startLongPress(touch);
            }}
            onTouchMove={(e) => {
                // якщо палець поїхав далеко — вважаємо, що це скрол, а не long-press
                const pt = longPressPointRef.current;
                if (!pt || e.touches.length !== 1) return;
                const t = e.touches[0];
                const dx = Math.abs(t.clientX - pt.x);
                const dy = Math.abs(t.clientY - pt.y);
                if (dx > 10 || dy > 10) {
                    cancelLongPress();
                }
            }}
            onTouchEnd={(e) => {
                // якщо спрацював long-press — гасимо клік
                if (longPressFiredRef.current) {
                    e.preventDefault();
                }
                cancelLongPress();
            }}
            onTouchCancel={() => {
                cancelLongPress();
            }}
        >
            <CrumbsGrid path={path} authed={authed} folders={folders} images={images} videos={videos} pdfs={pdfs} files={files} flatMode={flatMode} loading={loading} enterFolder={enterFolder} />
            {folders.length > 0 && (
                <FolderGrid
                    folders={folders}
                    onOpen={enterFolder}
                    isSelected={(fd) => isSelected('folder', fd.id)}
                    onItemClick={(fd, e) => itemHandlersFor('folder').onClick(fd, e)}
                    onItemContextMenu={(fd, e) => itemHandlersFor('folder').onContextMenu(fd, e)}
                />
            )}
            {images.length > 0 && (
                <ImageGrid
                    images={images}
                    onOpen={(i) => onOpenItem(i, 'image')}
                    isSelected={(it) => isSelected('image', it.id)}
                    onItemClick={(it, e) => itemHandlersFor('image').onClick(it, e)}
                    onItemContextMenu={(it, e) => itemHandlersFor('image').onContextMenu(it, e)}
                />
            )}
            {videos.length > 0 && (
                <VideoGrid
                    videos={videos}
                    onOpen={(i) => onOpenItem(i, 'video')}
                    isSelected={(it) => isSelected('video', it.id)}
                    onItemClick={(it, e) => itemHandlersFor('video').onClick(it, e)}
                    onItemContextMenu={(it, e) => itemHandlersFor('video').onContextMenu(it, e)}
                />
            )}
            {pdfs.length > 0 && (
                <PdfGrid
                    pdfs={pdfs}
                    onOpen={(i) => onOpenItem(i, 'pdf')}
                    isSelected={(it) => isSelected('pdf', it.id)}
                    onItemClick={(it, e) => itemHandlersFor('pdf').onClick(it, e)}
                    onItemContextMenu={(it, e) => itemHandlersFor('pdf').onContextMenu(it, e)}
                />
            )}

            {menu?.open && !isReadOnly && (
                <ContextMenu
                    menu={menu}
                    setMenu={setMenu}
                    selected={selected}
                    clearSelection={clearSelection}
                    openMoveDialog={openMoveDialog}
                    openDeleteConfirm={openDeleteConfirm}
                    getItemById={getItemById}
                    onRename={async (id) => {
                        const current = getItemByIdSafe(id);
                        const rect = findItemRectById(id);
                        if (!rect) return;
                        setMenu(null);
                        setRenameUi({ id, rect, initial: current?.name || '' });
                    }}
                />
            )}
            {renameUi && (
                <InlineRenamePopover
                    targetRect={renameUi.rect}
                    initial={renameUi.initial}
                    onSubmit={submitRename}
                    onCancel={() => setRenameUi(null)}
                />
            )}

            {/* Modals */}
            <ConfirmModal
                open={confirm.open}
                onCancel={() => confirm.resolve(false)}
                onConfirm={() => confirm.resolve(true)}
            />
            <MoveDialog
                open={moveOpen}
                path={path}
                authed={authed}
                folders={folders}
                enterFolder={enterFolder}
                onCancel={() => { setMoveOpen(false); setMoveCtx(''); clearSelection(); }}
                onConfirm={(targetId) => {
                    setMoveOpen(false);
                    onMoveItems(effectiveSelectedList, String(targetId), String(moveCtx));
                    clearSelection();
                    setMoveCtx('');
                    setActiveKey(null);
                }}
                excludeIds={excludeIds}
                createFolder={uploadLb.createFolder}
            />
            {/* Прямокутник виділення */}
            {
                box && (
                    <div
                        className="pointer-events-none fixed z-[60] border-2 border-blue-500/70 bg-blue-500/10"
                        style={{
                            left: `${box.left}px`,
                            top: `${box.top}px`,
                            width: `${box.width}px`,
                            height: `${box.height}px`,
                        }}
                    />
                )
            }
        </section>
    );
}

GallerySection.propTypes = {
    path: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired })).isRequired,
    authed: PropTypes.bool.isRequired,
    folders: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        // type: PropTypes.string
        type: PropTypes.string,
        isShortcut: PropTypes.bool,
        shortcutTargetId: PropTypes.string,
        shortcutTargetMime: PropTypes.string,
    })),
    images: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string, type: PropTypes.string, thumb: PropTypes.string, url: PropTypes.string, mtime: PropTypes.string })),
    videos: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string, type: PropTypes.string, thumb: PropTypes.string, url: PropTypes.string, mtime: PropTypes.string })),
    pdfs: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string, type: PropTypes.string, thumb: PropTypes.string, url: PropTypes.string, mtime: PropTypes.string })),
    files: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string, type: PropTypes.string, thumb: PropTypes.string, url: PropTypes.string, mtime: PropTypes.string })),
    enterFolder: PropTypes.func.isRequired,
    onOpenItem: PropTypes.func.isRequired,
    onDeleteItems: PropTypes.func,
    onMoveItems: PropTypes.func, // (items, targetFolderId)
    uploadLb: PropTypes.func,
    flatMode: PropTypes.bool,
    loading: PropTypes.bool,
    driveApi: PropTypes.object.isRequired,
    setError: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool,
};

GallerySection.defaultProps = {
    folders: [], images: [], videos: [], pdfs: [], files: [], flatMode: false, loading: false,
};

// ---- propTypes для ConfirmModal ----
ConfirmModal.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    danger: PropTypes.bool,
};

ConfirmModal.defaultProps = {
    title: "Видалити вибране?",
    description: "Дію не можна скасувати.",
    danger: true,
};
